import * as idb from 'idb';
import type { QueueItem, QueueStats, OfflineStorage } from '../types';

/**
 * IndexedDB storage implementation for offline queue
 * Uses separate object store from redux-persist
 */

const DB_NAME = 'pos360-offline-db';
const DB_VERSION = 1;
const STORE_NAME = 'sync-queue';

// Cache the DB promise
let dbPromise: Promise<idb.IDBPDatabase> | null = null;

const getDB = (): Promise<idb.IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = idb.openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
          // Create indexes for efficient querying
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('priority', 'priority', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Serialize Date objects for IndexedDB storage
 */
const serializeItem = (item: QueueItem): any => ({
  ...item,
  createdAt: item.createdAt.toISOString(),
  updatedAt: item.updatedAt.toISOString(),
  lastErrorAt: item.lastErrorAt?.toISOString(),
});

/**
 * Deserialize Date objects from IndexedDB
 */
const deserializeItem = (data: any): QueueItem => ({
  ...data,
  createdAt: new Date(data.createdAt),
  updatedAt: new Date(data.updatedAt),
  lastErrorAt: data.lastErrorAt ? new Date(data.lastErrorAt) : undefined,
});

export const idbQueueStorage: OfflineStorage = {
  /**
   * Get a single item by ID
   */
  async getItem(id: string): Promise<QueueItem | null> {
    try {
      const db = await getDB();
      const data = await db.get(STORE_NAME, id);
      return data ? deserializeItem(data) : null;
    } catch (error) {
      console.error('[OfflineStorage] Failed to get item:', error);
      return null;
    }
  },

  /**
   * Get all items in the queue
   */
  async getAllItems(): Promise<QueueItem[]> {
    try {
      const db = await getDB();
      const data = await db.getAll(STORE_NAME);
      return data.map(deserializeItem);
    } catch (error) {
      console.error('[OfflineStorage] Failed to get all items:', error);
      return [];
    }
  },

  /**
   * Get pending items (sorted by priority desc, then createdAt asc)
   */
  async getPendingItems(): Promise<QueueItem[]> {
    try {
      const db = await getDB();
      const items = await db.getAllFromIndex(STORE_NAME, 'status', 'pending');
      return items
        .map(deserializeItem)
        .sort((a, b) => {
          // Higher priority first
          if (b.priority !== a.priority) {
            return b.priority - a.priority;
          }
          // Older items first
          return a.createdAt.getTime() - b.createdAt.getTime();
        });
    } catch (error) {
      console.error('[OfflineStorage] Failed to get pending items:', error);
      return [];
    }
  },

  /**
   * Get failed items that can be retried
   */
  async getFailedItems(): Promise<QueueItem[]> {
    try {
      const db = await getDB();
      const items = await db.getAllFromIndex(STORE_NAME, 'status', 'failed');
      return items
        .map(deserializeItem)
        .filter((item) => item.retryCount < item.maxRetries)
        .sort((a, b) => b.priority - a.priority);
    } catch (error) {
      console.error('[OfflineStorage] Failed to get failed items:', error);
      return [];
    }
  },

  /**
   * Get retrying items that can be retried
   */
  async getRetryingItems(): Promise<QueueItem[]> {
    try {
      const db = await getDB();
      const items = await db.getAllFromIndex(STORE_NAME, 'status', 'retrying');
      return items
        .map(deserializeItem)
        .filter((item) => item.retryCount < item.maxRetries)
        .sort((a, b) => b.priority - a.priority);
    } catch (error) {
      console.error('[OfflineStorage] Failed to get retying items:', error);
      return [];
    }
  },

  /**
   * Add a new item to the queue
   */
  async addItem(item: QueueItem): Promise<void> {
    try {
      const db = await getDB();
      await db.put(STORE_NAME, serializeItem(item));
    } catch (error) {
      console.error('[OfflineStorage] Failed to add item:', error);
      throw error;
    }
  },

  /**
   * Update an existing item
   */
  async updateItem(id: string, updates: Partial<QueueItem>): Promise<void> {
    try {
      const db = await getDB();
      const existing = await db.get(STORE_NAME, id);

      if (!existing) {
        throw new Error(`Item with id ${id} not found`);
      }

      const updated = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await db.put(STORE_NAME, updated);
    } catch (error) {
      console.error('[OfflineStorage] Failed to update item:', error);
      throw error;
    }
  },

  /**
   * Remove an item from the queue
   */
  async removeItem(id: string): Promise<void> {
    try {
      const db = await getDB();
      await db.delete(STORE_NAME, id);
    } catch (error) {
      console.error('[OfflineStorage] Failed to remove item:', error);
      throw error;
    }
  },

  /**
   * Clear all completed items
   * Returns the number of items removed
   */
  async clearCompleted(): Promise<number> {
    try {
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const completedItems = await store.index('status').getAllKeys('completed');

      for (const key of completedItems) {
        await store.delete(key);
      }

      await tx.done;
      return completedItems.length;
    } catch (error) {
      console.error('[OfflineStorage] Failed to clear completed items:', error);
      return 0;
    }
  },

  /**
   * Get queue statistics
   */
  async getStats(): Promise<QueueStats> {
    try {
      const db = await getDB();
      const allItems = await db.getAll(STORE_NAME);

      return {
        total: allItems.length,
        pending: allItems.filter((i) => i.status === 'pending').length,
        processing: allItems.filter((i) => i.status === 'processing').length,
        completed: allItems.filter((i) => i.status === 'completed').length,
        failed: allItems.filter((i) => i.status === 'failed').length,
        retrying: allItems.filter((i) => i.status === 'retrying').length,
      };
    } catch (error) {
      console.error('[OfflineStorage] Failed to get stats:', error);
      return {
        total: 0,
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        retrying: 0,
      };
    }
  },
};

/**
 * Delete the entire offline database
 * Use with caution - mainly for testing or logout
 */
export const deleteOfflineDB = async (): Promise<void> => {
  try {
    dbPromise = null;
    await idb.deleteDB(DB_NAME);
  } catch (error) {
    console.error('[OfflineStorage] Failed to delete database:', error);
    throw error;
  }
};

export default idbQueueStorage;
